import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useEffect, useState } from 'react';
import dayjs from 'dayjs';
import { styled } from '@mui/system';
import MuiGrid from '@mui/material/Grid';
import DateRangeSharp from '@mui/icons-material/DateRangeSharp';
import { DatePicker as MuiDatePicker, PickersLayout, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import IconButton from '@mui/material/IconButton';
import InputLabel from '@cpg-its/ads.form.input-label';
import Typography from '@cpg-its/ads.common.typography';
import { blue, darkBlue, darkGrey, extraLightGrey, red, white } from '@cpg-its/ads.constants.colors';
import StyledPicker from './styled-picker';
const errorStyles = {
    errorInput: {
        '& div.MuiInputBase-root, input:focus': {
            border: `1px solid ${red} !important`,
        },
        '& svg.MuiSvgIcon-root': {
            color: `${red} !important`,
        },
    },
};
const StyledDatePicker = StyledPicker(MuiDatePicker);
const StyledPickersLayout = styled(PickersLayout)({
    '.MuiTouchRipple-root': {
        display: 'none',
    },
    '.MuiDateCalendar-root': {
        maxHeight: '356px',
    },
    '.MuiPickersCalendarHeader-root': {
        color: white,
        backgroundColor: darkBlue,
        borderRadius: '4px 4px 0 0',
        marginTop: 0,
        marginBottom: '.5rem',
        padding: '.5rem .5rem .5rem 1.5rem',
    },
    '.MuiPickersCalendarHeader-label': {
        fontSize: '18px',
    },
    '.MuiPickersCalendarHeader-switchViewButton, .MuiPickersArrowSwitcher-button': {
        color: white,
    },
    '.MuiDayCalendar-weekDayLabel': {
        color: darkGrey,
    },
    '.MuiPickersDay-root': {
        color: blue,
        '&:hover': {
            backgroundColor: extraLightGrey,
        },
    },
    '.MuiPickersDay-today': {
        border: 'none',
    },
    '.Mui-selected': {
        color: white,
        '&:hover': {
            backgroundColor: darkBlue,
        },
    },
});
const FooterWrapper = styled(MuiGrid)({
    marginTop: '0.25rem',
});
const createRenderDatePicker = (props) => (_jsx(StyledDatePicker, { "data-formdata": props.name, "data-testid": props.testId, dayOfWeekFormatter: (day) => dayjs(day).format('dd'), format: "MM/dd/yyyy", onChange: props.handleChange, onOpen: props.handleOpen, reduceAnimations: true, slots: {
        layout: StyledPickersLayout,
        openPickerIcon: DateRangeSharp,
        openPickerButton: (buttonProps) => {
            if (!props.noCalendarIcon) {
                return (_jsx(IconButton, { ...buttonProps, disableRipple: true, children: buttonProps.children }));
            }
            return null;
        },
    }, value: props.inputValue, disabled: props.disabled, disableFuture: props.disableFuture, ...props.datePickerProps }));
export const DatePickerComponent = ({ beenTouched = false, disabled = false, disableFuture = false, field: { name, value, } = {}, form: { setFieldTouched, setFieldValue } = {}, hasError = false, label = '', noCalendarIcon = false, noSubText = false, onBlur = undefined, required = false, subLabel = '', testId, tooltip = {}, }) => {
    const conformString = (date) => dayjs(date).format('MM/DD/YYYY');
    const convertStringToObject = (date) => new Date(conformString(date));
    const maskedDate = typeof value === 'string' && value.charAt(0).toUpperCase() === 'X';
    const originalValue = React.useMemo(() => value || (value === '' ? '' : null), []);
    const [error, setError] = useState(null);
    const [displayError, setDisplayError] = useState(false);
    const [calendarOpen, setCalendarOpen] = useState(false);
    const [inputValue, setInputValue] = useState(value ? convertStringToObject(value) : null);
    useEffect(() => {
        if (value === originalValue) {
            setError(null);
            setDisplayError(false);
            setCalendarOpen(false);
            setInputValue(value ? convertStringToObject(value) : null);
        }
    }, [value]);
    const errorMessage = React.useMemo(() => {
        switch (error) {
            case 'maxDate': {
                return 'Please enter a year before 2100';
            }
            case 'minDate': {
                return 'Please enter a year after 1899';
            }
            case 'invalidDate': {
                return 'Invalid date';
            }
            default: {
                return '';
            }
        }
    }, [error]);
    const handleOpen = () => {
        setCalendarOpen(true);
    };
    // eslint-disable-next-line max-len
    const handleChange = (dateObject, { validationError }) => {
        if (dateObject instanceof Date) {
            const dateString = dayjs(dateObject).format('MM/DD/YYYY');
            if (displayError) {
                setDisplayError(false);
            }
            setError(validationError);
            if (onBlur && calendarOpen) {
                onBlur({
                    target: {
                        value: dateString,
                    },
                });
            }
            if (calendarOpen) {
                setCalendarOpen(false);
            }
            if (setFieldValue && name) {
                if (dateString.length === 10) {
                    setInputValue(dateObject);
                    return setFieldValue(name, dateString);
                }
                if (maskedDate) {
                    setInputValue(null);
                    return setFieldValue(name, null);
                }
                setFieldValue(name, null);
            }
            return setInputValue(dateObject);
        }
        if (setFieldValue && name) {
            setFieldValue(name, null);
        }
        setDisplayError(false);
        return setInputValue(null);
    };
    const handleBlur = (event) => {
        if (setFieldTouched && name) {
            setFieldTouched(name, true);
        }
        if (onBlur) {
            onBlur(event);
        }
        if (inputValue !== null && errorMessage) {
            setDisplayError(true);
        }
        else {
            setDisplayError(false);
        }
    };
    const inputProps = React.useMemo(() => {
        if (typeof value === 'string') {
            if (value.charAt(0).toUpperCase() === 'X') {
                return ({
                    value: value.toUpperCase(),
                });
            }
            if (value) {
                return ({
                    value: conformString(value),
                });
            }
        }
        return null;
    }, [value]);
    const handleKeyDown = (e) => {
        if (maskedDate && (e.key.match(/[0-9]/) || e.key === 'Backspace')) {
            if (setFieldValue && name) {
                setFieldValue(name, null);
            }
            setInputValue(null);
        }
    };
    const renderLabel = React.useMemo(() => {
        if (label || tooltip) {
            return (_jsx(InputLabel, { label: label, subLabel: subLabel, required: required, tooltip: tooltip }));
        }
        return null;
    }, [label, tooltip, subLabel, required]);
    const datePickerProps = {
        sx: beenTouched && hasError ? errorStyles.errorInput : {},
        slotProps: {
            popper: {
                placement: 'bottom-end',
            },
            textField: {
                InputProps: {},
                inputProps: {
                    'data-testid': testId,
                    ...inputProps,
                },
                name,
                placeholder: 'MM/DD/YYYY',
                onBlur: handleBlur,
                onKeyDown: handleKeyDown,
                helperText: displayError ? errorMessage : null,
                sx: {
                    '& .MuiFormHelperText-root': {
                        marginLeft: 0,
                    },
                },
            },
        },
    };
    const renderDatePicker = createRenderDatePicker({
        name,
        testId,
        handleChange,
        handleOpen,
        noCalendarIcon,
        inputValue,
        disabled,
        disableFuture,
        datePickerProps,
    });
    const renderFooter = React.useMemo(() => {
        if (!noSubText) {
            return (_jsx(FooterWrapper, { children: _jsx(Typography, { color: darkGrey, text: "MM/DD/YYYY", size: "small" }) }));
        }
        return null;
    }, [noSubText]);
    return (_jsxs(_Fragment, { children: [_jsxs(LocalizationProvider, { dateAdapter: AdapterDateFns, children: [renderLabel, renderDatePicker] }), renderFooter] }));
};
export default DatePickerComponent;
//# sourceMappingURL=date-picker.js.map